import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/nodes';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">该文章可能已被移除或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://your-domain.com/article/${article.slug}`,
    articleBody: article.content.replace(/<[^>]*>/g, '').substring(0, 2000),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://your-domain.com/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `https://your-domain.com/category/${article.category}`, name: article.category } },
      { '@type': 'ListItem', position: 3, item: { '@id': `https://your-domain.com/article/${article.slug}`, name: article.title } },
    ],
  };

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && (a.category === article.category || a.tags?.some((t) => article.tags?.includes(t))))
    .slice(0, 3);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mt-6 mb-8">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> {article.readTime} 分钟阅读
              </span>
            </div>
            {article.tags && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-800 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          {/* Article Content */}
          <article className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-14">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.slug} {...a} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}